import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';

import { DeviceUiPage, DeviceUiRoute, DeviceUiSharedDeviceBehaviorPlugin } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { CoverageMode, DeviceModel, RotationAxis } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { DeviceCoverageDeviceBehaviorPlugin } from '@shure/shared/angular/features/device-coverage/data-access';
import { MicOrientation, MicView } from '@shure/shared/angular/features/device-coverage/feature-coverage-dca';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import { LightsSettingsSupportType } from '@shure/shared/angular/features/device-lights-settings/data-access';
import { ConfirmDialogData, ShowIcon } from '@shure/shared/angular/ui/components/dialogs/confirm';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';
import { PositionUnit } from '@shure/shared/angular/utils/utils-position';

const DCA901_LINE_IMAGE_ASSETS = new Set([
	'device-dca901-back-line.svg',
	'device-dca901-front-line.svg',
	'device-dca901-front-0-line.svg',
	'device-dca901-front-90-line.svg',
	'device-dca901-front-180-line.svg',
	'device-dca901-front-270-line.svg',
	'device-dca901-side-0-line.svg',
	'device-dca901-side-90-line.svg',
	'device-dca901-side-180-line.svg',
	'device-dca901-side-270-line.svg',
	'device-dca901-profile-line.svg',
	'device-dca901-profile-led-line.svg'
]);

const withVirtualFallback = (live: string): string => {
	const line = live.replace('.svg', '-line.svg');
	return DCA901_LINE_IMAGE_ASSETS.has(line) ? line : live;
};

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Dca901DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSharedDeviceBehaviorPlugin,
		DeviceCoverageDeviceBehaviorPlugin,
		CoverageImageProvider
{
	public readonly models = [DeviceModel.DCA901];

	constructor(protected readonly transloco: TranslocoService) {}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return of([
			{
				route: DeviceUiRoute.CoverageDca901,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.coverage')
			},
			{
				route: DeviceUiRoute.ChannelsDca901,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.channels')
			},
			{
				route: DeviceUiRoute.Presets,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.presets')
			},
			{
				route: DeviceUiRoute.Settings,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.settings')
			}
		]);
	}

	public getCoverageImage(
		_model: DeviceModel,
		micView?: MicView | 'round-front' | 'profile' | 'profile-led',
		micOrientation?: MicOrientation
	): CoverageImage {
		if (micView === 'round-front') {
			const live = 'device-dca901-front.svg';
			return { live, virtual: withVirtualFallback(live), isImageRound: true };
		}
		if (micView === 'profile') {
			const live = 'device-dca901-profile.svg';
			return {
				live,
				virtual: withVirtualFallback(live),
				isImageRound: false
			};
		}
		if (micView === 'profile-led') {
			const live = 'device-dca901-profile-led.svg';
			return {
				live,
				virtual: withVirtualFallback(live),
				isImageRound: false
			};
		}
		if (micView) {
			const live = `device-dca901-${micView}-${micOrientation}.svg`;
			return {
				live,
				virtual: withVirtualFallback(live),
				isImageRound: false
			};
		}

		const live = 'device-dca901-back.svg';
		return {
			live,
			virtual: withVirtualFallback(live),
			isImageRound: true
		};
	}

	public getDefaultRoute(_model: DeviceModel, _coverageMode?: CoverageMode | undefined): DeviceUiRoute | undefined {
		return DeviceUiRoute.CoverageDca901;
	}

	public getChannelsRoute(): DeviceUiRoute {
		return DeviceUiRoute.ChannelsDca901;
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedLegacy;
	}

	public showNoiseReductionIntensityLevel(): boolean {
		return true;
	}

	public usePostGateValues(): boolean {
		return false;
	}

	public supportsChannels(): boolean {
		return true;
	}

	public supportsDeviceLocalRotation(): boolean {
		return true;
	}

	public getDeviceLocalRotationIllustration(axis: RotationAxis): string {
		return `./assets/images/devices/device-dca901-${axis[0]}-rotation.gif`;
	}

	public getEqualizerBypassConfirmDialog(): ConfirmDialogData {
		return {
			title: this.transloco.translate('scDevUiSharedDevBehavior.dsp.bypass-peq-confirm-dialog-title'),
			body: this.transloco.translate('scDevUiSharedDevBehavior.dsp.bypass-peq-confirm-dialog-body'),
			cancelLabel: this.transloco.translate('scDevUiSharedDevBehavior.common.buttons.cancel'),
			confirmLabel: this.transloco.translate(
				'scDevUiSharedDevBehavior.dsp.bypass-peq-confirm-dialog-confirm-button'
			),
			doNotShowDialogId: 'turnoff-bypass-eq',
			showIcon: ShowIcon.Warning
		};
	}

	public getDefaultHeight(): PositionUnit {
		return PositionUnit.fromRaw(2743);
	}
}
